import { insertNewRow } from "../insertRows";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { branchNameError } from "../branchName";
import { copyAllCommitFiles } from "../commitFiles";
import { zql } from "../generatedDrizzleSchema";
import { inheritBranchContributors } from "../inheritBranchContributors";
import { actorColumns, creatorColumns } from "../rbac/roles";
import { stripNullBytes } from "../stripNullBytes";
import {
  assertChatAttachmentsAllowed,
  attachmentSchema,
} from "./attachmentValidation";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export const createBranchSchema = z.object({
  branchId: clientGeneratableIdSchema,
  commitId: clientGeneratableIdSchema,
  chatId: clientGeneratableIdSchema,
  workflowId: z.string(),
  name: z.string(),
  userEntryId: clientGeneratableIdSchema.optional(),
  prompt: z.string().transform(stripNullBytes).optional(),
  isDictated: z.boolean().optional(),
  planMode: z.boolean().optional(),
  actionKind: z.enum(["sub_agent"]).optional(),
  attachments: z.array(attachmentSchema).optional(),
  previousBranchId: z.string().optional(),
  sourceCommitId: z.string().optional(),
});

export const createBranch = mutator({
  schema: createBranchSchema,
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    previousBranch: {
      kind: "branch",
      from: "previousBranchId",
      optional: true,
    },
    sourceCommit: { kind: "commit", from: "sourceCommitId", optional: true },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      branchId,
      commitId,
      chatId,
      workflow,
      name,
      userEntryId,
      prompt,
      isDictated,
      planMode,
      actionKind,
      attachments,
      previousBranch,
      sourceCommit,
    },
  }) => {
    const creator = creatorColumns(ctx);
    const actor = actorColumns(ctx);
    const nameError = branchNameError(name);
    if (nameError) throw userFacingError(nameError);
    assertChatAttachmentsAllowed(attachments);

    const now = Date.now();
    const hasPrompt = !!(userEntryId && prompt);
    const lastChatEntryAt = hasPrompt ? now : null;

    const [mainBranch] = await tx.run(
      zql.workflowBranches
        .where("workflowId", workflow.id)
        .where("name", "main")
    );

    let sourceCommitId: string;
    let forkCommitId: string | null;

    if (sourceCommit) {
      if (sourceCommit.branch.workflowId !== workflow.id) {
        throw userFacingError("Source commit not found");
      }
      sourceCommitId = sourceCommit.id;
      forkCommitId = mainBranch?.headCommitId ?? null;
    } else {
      if (!mainBranch?.headCommitId) {
        throw userFacingError("Main branch not found");
      }
      sourceCommitId = mainBranch.headCommitId;
      forkCommitId = mainBranch.headCommitId;
      if (previousBranch) {
        if (previousBranch.workflowId !== workflow.id) {
          throw userFacingError("Previous branch not found");
        }
        if (previousBranch.headCommitId) {
          sourceCommitId = previousBranch.headCommitId;
          forkCommitId = previousBranch.headCommitId;
        }
      }
    }

    await insertNewRow(tx, "workflowBranches", {
      id: branchId,
      workflowId: workflow.id,
      userId: actor.userId,
      serviceAccountId: actor.serviceAccountId,
      name,
      headCommitId: commitId,
      forkCommitId,
      createdAt: now,
      lastChatEntryAt,
      tenantId: ctx.tenantId,
    });

    const sourceBranch =
      sourceCommit?.branch ??
      (previousBranch?.headCommitId ? previousBranch : mainBranch!);
    await inheritBranchContributors(
      tx,
      ctx,
      { ...sourceBranch, workflow },
      branchId
    );

    if (lastChatEntryAt) {
      await tx.mutate.workflows.update({
        id: workflow.id,
        updatedAt: now,
      });
    }

    // Create initial checkpoint for the branch
    await insertNewRow(tx, "workflowCommits", {
      id: commitId,
      workflowId: workflow.id,
      branchId,
      parentCommitId: sourceCommitId,
      chatId,
      message: "Starting point",
      createdAt: now,
      createdByUserId: creator.createdByUserId,
      createdByServiceAccountId: creator.createdByServiceAccountId,
      tenantId: ctx.tenantId,
    });

    // Copy all files from source to the new commit
    await copyAllCommitFiles(
      { tx, commitId: sourceCommitId, tenantId: ctx.tenantId },
      commitId
    );

    await insertNewRow(tx, "chats", {
      id: chatId,
      commitId,
      createdAt: now,
      lastChatEntryType: hasPrompt ? "UserMessage" : null,
      respondingAt: prompt ? now : null,
      tenantId: ctx.tenantId,
    });

    if (userEntryId && prompt) {
      await insertNewRow(tx, "chatEntries", {
        id: userEntryId,
        chatId,
        entryType: "UserMessage",
        createdAt: now,
        previousEntryId: null,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.userMessages.insert({
        entryId: userEntryId,
        userId: actor.userId,
        serviceAccountId: actor.serviceAccountId,
        text: prompt,
        isDictated: isDictated ?? null,
        planMode: planMode ?? null,
        actionKind: actionKind ?? null,
        tenantId: ctx.tenantId,
      });
      if (actor.userId) {
        await tx.mutate.branchContributors.update({
          branchId,
          userId: actor.userId,
          lastMessagedAt: now,
        });
      }
      for (const att of attachments ?? []) {
        await insertNewRow(tx, "attachments", {
          id: att.id,
          entryId: userEntryId,
          fileName: att.fileName,
          mediaType: att.mediaType,
          sizeBytes: att.sizeBytes,
          tenantId: ctx.tenantId,
        });
      }
    }
  },
});
